/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: GeoFirewallPage.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    GeoFirewall page - Interactive world map for blocking/unblocking countries
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Globe, Shield, ZoomIn, ZoomOut, Search, Check, ChevronsUpDown, RotateCcw } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { ModuleFormHeader } from '../../components/layouts';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { WorldMap, WorldMapRef } from './components/WorldMap';
import { fetchGeoFirewallRules, createGeoFirewallRule, deleteGeoFirewallRule, getGeoFirewallStatus, applyGeoFirewallRules } from './api/geoFirewallApi';
import type { GeoFirewallRule } from './types';
import { COUNTRIES } from '../../data/countries';

// Simple Country Search Component
function CountrySearch({
  countries,
  onSelect,
  t
}: {
  countries: typeof COUNTRIES,
  onSelect: (code: string) => void,
  t: (key: string) => string
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [search, setSearch] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    if (!search) return countries;
    const lower = search.toLowerCase();
    return countries.filter(c =>
      c.name.toLowerCase().includes(lower) ||
      c.code.toLowerCase().includes(lower)
    );
  }, [countries, search]);

  return (
    <div className="relative w-[250px]" ref={wrapperRef}>
      <Button
        variant="outline"
        aria-expanded={open}
        className="w-full justify-between bg-background/50 backdrop-blur border-border/50 h-9"
        onClick={() => setOpen(!open)}
      >
        {value
          ? countries.find((c) => c.code === value)?.name
          : t('geoFirewall.map.searchCountry')}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 z-50 w-full rounded-md border border-border bg-popover text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95">
          <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={t('geoFirewall.map.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-[300px] overflow-y-auto overflow-x-hidden py-1">
            {filtered.length === 0 && (
              <div className="py-6 text-center text-sm text-balance">
                {t('common.noResults')}
              </div>
            )}
            {filtered.map((country) => (
              <div
                key={country.code}
                className={cn(
                  "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                  value === country.code && "bg-accent text-accent-foreground"
                )}
                onClick={() => {
                  setValue(country.code);
                  onSelect(country.code);
                  setOpen(false);
                  setSearch("");
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === country.code ? "opacity-100" : "opacity-0"
                  )}
                />
                <span className="truncate">{country.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function GeoFirewallPage() {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const [rules, setRules] = useState<GeoFirewallRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blockedCountriesCount, setBlockedCountriesCount] = useState(0);
  // Local state for selected countries (not saved yet)
  const [localSelectedCountries, setLocalSelectedCountries] = useState<string[]>([]);
  const [currentSelectedCountries, setCurrentSelectedCountries] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [mapAvailableCountries, setMapAvailableCountries] = useState<{ code: string; name: string }[]>([]);
  const worldMapRef = useRef<WorldMapRef>(null);

  useFixedActionBar(true);

  // Load all rules on mount
  useEffect(() => {
    loadRules();
  }, []);

  // GeoFirewall is enabled if there are blocked countries
  const geoFirewallEnabled = blockedCountriesCount > 0;

  const loadRules = async () => {
    try {
      setLoading(true);
      // Fetch all rules (use max limit allowed by API: 100)
      const result = await fetchGeoFirewallRules({
        page: 1,
        limit: 100,
        sortBy: 'priority',
        sortOrder: 'asc',
      });
      let allRules = [...result.data];

      // If there are more pages, fetch them too
      if (result.pagination.totalPages > 1) {
        for (let page = 2; page <= result.pagination.totalPages; page++) {
          const pageResult = await fetchGeoFirewallRules({
            page,
            limit: 100,
            sortBy: 'priority',
            sortOrder: 'asc',
          });
          allRules.push(...pageResult.data);
        }
      }

      setRules(allRules);

      // Initialize local selected countries from loaded rules
      const loadedBlockedCountries = allRules
        .filter((rule) => rule.action === 'block' && rule.enabled)
        .map((rule) => rule.countryCode);

      // Use setTimeout to ensure state updates are processed and map is ready
      setTimeout(() => {
        setLocalSelectedCountries(loadedBlockedCountries);
        setCurrentSelectedCountries(loadedBlockedCountries);
        setHasUnsavedChanges(false);
      }, 0);
    } catch (error) {
      showError(error instanceof Error ? error.message : t('geoFirewall.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Get blocked countries from rules
  const blockedCountries = useMemo(() => {
    return rules
      .filter((rule) => rule.action === 'block' && rule.enabled)
      .map((rule) => rule.countryCode);
  }, [rules]);

  // Handle map data loaded
  const handleMapDataLoaded = useCallback((countries: { code: string; name: string }[]) => {
    setMapAvailableCountries(countries);
  }, []);

  // Handle country click - toggle block/unblock (local state only)
  const handleCountryClick = useCallback(
    (countryCode: string, countryName: string) => {
      setLocalSelectedCountries((prev) => {
        const isSelected = prev.includes(countryCode);
        const newSelection = isSelected
          ? prev.filter((code) => code !== countryCode)
          : [...prev, countryCode];

        // Check if there are unsaved changes
        const currentBlockedCountries = rules
          .filter((rule) => rule.action === 'block' && rule.enabled)
          .map((rule) => rule.countryCode);
        const hasChanges = JSON.stringify([...currentBlockedCountries].sort()) !==
          JSON.stringify([...newSelection].sort());
        setHasUnsavedChanges(hasChanges);

        return newSelection;
      });
    },
    [rules]
  );

  // Handle select all countries (local state only)
  const handleSelectAll = useCallback(() => {
    // Combine both map-detected countries and static list to ensure we don't miss anything.
    // This fixes issues where the map scanner might miss a region (e.g. IR) but we know it exists,
    // or conversely where the map has a region not in our static list.
    const allCodes = new Set<string>();

    // Add all map detect countries
    mapAvailableCountries.forEach(c => allCodes.add(c.code));

    // Add all static countries
    COUNTRIES.forEach(c => allCodes.add(c.code));

    const allCountryCodes = Array.from(allCodes);
    setLocalSelectedCountries(allCountryCodes);

    // Check if there are unsaved changes
    const currentBlockedCountries = rules
      .filter((rule) => rule.action === 'block' && rule.enabled)
      .map((rule) => rule.countryCode);
    const hasChanges = JSON.stringify([...currentBlockedCountries].sort()) !==
      JSON.stringify([...allCountryCodes].sort());
    setHasUnsavedChanges(hasChanges);
  }, [rules, mapAvailableCountries]);

  // Handle unselect all countries (local state only)
  const handleUnselectAll = useCallback(() => {
    setLocalSelectedCountries([]);

    // Check if there are unsaved changes
    const currentBlockedCountries = rules
      .filter((rule) => rule.action === 'block' && rule.enabled)
      .map((rule) => rule.countryCode);
    const hasChanges = currentBlockedCountries.length > 0;
    setHasUnsavedChanges(hasChanges);
  }, [rules]);



  // Handle save - apply all local changes to the database
  const handleSave = useCallback(async () => {
    try {
      setSaving(true);

      // Get current blocked countries from database
      const currentBlockedCountries = rules
        .filter((rule) => rule.action === 'block' && rule.enabled)
        .map((rule) => rule.countryCode);

      // Find countries to add (in local selection but not in database)
      const countriesToAdd = localSelectedCountries.filter(
        (code) => !currentBlockedCountries.includes(code)
      );

      // Find countries to remove (in database but not in local selection)
      const countriesToRemove = currentBlockedCountries.filter(
        (code) => !localSelectedCountries.includes(code)
      );

      // Remove rules for countries that are no longer selected
      for (const countryCode of countriesToRemove) {
        const ruleToDelete = rules.find(
          (rule) => rule.countryCode === countryCode && rule.action === 'block'
        );
        if (ruleToDelete) {
          await deleteGeoFirewallRule(ruleToDelete.id);
        }
      }

      // Add new rules for newly selected countries
      for (const countryCode of countriesToAdd) {
        // Find country name from COUNTRIES list
        const country = COUNTRIES.find(c => c.code === countryCode);
        const countryName = country?.name || countryCode;

        await createGeoFirewallRule({
          name: `Block ${countryName}`,
          countryCode,
          countryName,
          action: 'block',
          direction: 'input',
          enabled: true,
          priority: 1,
        });
      }

      // Fetch updated rules WITHOUT triggering loading state (to preserve map colors)
      const result = await fetchGeoFirewallRules({
        page: 1,
        limit: 100,
        sortBy: 'priority',
        sortOrder: 'asc',
      });
      let allRules = [...result.data];

      // If there are more pages, fetch them too
      if (result.pagination.totalPages > 1) {
        for (let page = 2; page <= result.pagination.totalPages; page++) {
          const pageResult = await fetchGeoFirewallRules({
            page,
            limit: 100,
            sortBy: 'priority',
            sortOrder: 'asc',
          });
          allRules.push(...pageResult.data);
        }
      }

      // Update rules state
      setRules(allRules);

      // Update local selected countries from the newly loaded rules
      const loadedBlockedCountries = allRules
        .filter((rule) => rule.action === 'block' && rule.enabled)
        .map((rule) => rule.countryCode);
      setLocalSelectedCountries(loadedBlockedCountries);
      setBlockedCountriesCount(loadedBlockedCountries.length);

      setHasUnsavedChanges(false);
      showSuccess(t('geoFirewall.messages.saveSuccess'));

      // Apply changes to the system firewall
      try {
        await applyGeoFirewallRules();
        showSuccess(t('geoFirewall.messages.appliedToFirewall'));
      } catch (err) {
        // Show warning but don't fail the save operation
        showError(t('geoFirewall.messages.applyError') + ': ' + (err instanceof Error ? err.message : ''));
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : t('geoFirewall.messages.error'));
    } finally {
      setSaving(false);
    }
  }, [localSelectedCountries, rules, showSuccess, showError, t]);

  // Get selected countries (use local state if there are unsaved changes, otherwise use database)
  const selectedCountries = useMemo(() => {
    return hasUnsavedChanges ? localSelectedCountries : blockedCountries;
  }, [hasUnsavedChanges, localSelectedCountries, blockedCountries]);

  // Disable main scroll when component mounts
  useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.style.overflow = 'hidden';
      mainElement.style.overflowY = 'hidden';
      return () => {
        mainElement.style.overflow = '';
        mainElement.style.overflowY = '';
      };
    }
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <ModuleFormHeader
          icon={<Shield className="h-5 w-5 text-primary" />}
          title={t('geoFirewall.title')}
          hideBackButton={true}
          onBack={() => { }}
          backButtonText=""
        />
        <div className="mt-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Globe className="h-12 w-12 mx-auto mb-4 animate-spin text-primary" />
              <p className="text-muted-foreground">{t('common.loading')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col" style={{ height: 'calc(100vh - 80px)', overflow: 'hidden', maxHeight: 'calc(100vh - 80px)' }}>
      <ModuleFormHeader
        icon={<Shield className="h-5 w-5 text-primary" />}
        title={t('geoFirewall.title')}
        subtitle={t('geoFirewall.subtitle')}
        hideBackButton={true}
        onBack={() => { }}
        backButtonText=""
        actions={[
          {
            icon: <Globe className="h-4 w-4 text-red-600 dark:text-red-400" />,
            tooltip: t('geoFirewall.map.selectAll'),
            onClick: handleSelectAll,
            disabled: saving,
          },
          {
            icon: <Globe className="h-4 w-4 text-blue-600 dark:text-blue-400" />,
            tooltip: t('geoFirewall.map.unselectAll'),
            onClick: handleUnselectAll,
            disabled: saving,
          },

        ]}
        searchInput={
          <CountrySearch
            countries={COUNTRIES}
            onSelect={(code) => worldMapRef.current?.focusCountry(code)}
            t={t}
          />
        }
      />

      <div className="mt-4 -mx-6 flex-1 flex flex-col overflow-hidden" style={{ minHeight: 0 }}>
        {/* Map Container */}
        <div
          className="relative overflow-hidden flex-1 pt-8 pb-24"
          style={{ minHeight: 0 }}
        >
          <WorldMap
            ref={worldMapRef}
            selectedCountries={selectedCountries}
            onCountryClick={handleCountryClick}
            onMapDataLoaded={handleMapDataLoaded}
            initialSelectedCountries={currentSelectedCountries}
          />
        </div>
      </div>

      <FixedActionBar
        onSave={handleSave}
        showCancel={false}
        saveDisabled={saving || loading || !hasUnsavedChanges}
        isLoading={saving}
        saveText={saving ? t('common.saving') : t('common.save')}
      >
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="secondary"
            className="h-8 w-8"
            onClick={() => worldMapRef.current?.zoomIn()}
            title={t('geoFirewall.map.zoomIn')}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="secondary"
            className="h-8 w-8"
            onClick={() => worldMapRef.current?.zoomOut()}
            title={t('geoFirewall.map.zoomOut')}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="secondary"
            className="h-8 w-8"
            onClick={() => worldMapRef.current?.reset()}
            title={t('geoFirewall.map.resetZoom')}
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </FixedActionBar>
    </div>
  );
}
